import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import {getProcessCumulative, getServiceCurrent} from "../../api/static/StaticService";
import ChartLoadingOverlay from "../../components/Utils/ChartLoadingOverlay";
import {getDate} from "../../redux/slice/dateSlice";

const TABS = [
    {id: "inspection", label: "점검처리", dateKey: "점검처리현황", fetch: getProcessCumulative},
    {id: "incident", label: "장애처리", dateKey: "장애처리현황", fetch: getProcessCumulative},
    {id: "work", label: "작업처리", dateKey: "작업처리현황", fetch: getProcessCumulative},
    {id: "service", label: "서비스요청", dateKey: "서비스요청 처리현황", fetch: getServiceCurrent},
];

const BAR_BLUE = "#3b82f6";
const BAR_TRACK = "#ececec";
const AXIS_COLOR = "#666666";
const GRID_COLOR = "#e0e0e0";
const Y_TICKS = [0, 20, 40, 60, 80, 100];

const calcPercent = (done, total) => {
    const denominator = Number(total) || 0;
    const cnt = Number(done) || 0;
    if (denominator === 0) return 0;
    return Math.min(100, Math.round((cnt / denominator) * 100));
};

const truncateLabel = (name, maxLen = 3) => {
    if (!name) return "";
    return name.length > maxLen ? `${name.slice(0, maxLen)}...` : name;
};

export const buildHandleChartRows = (completeList, registeredList) => {
    const registeredByName = new Map((registeredList ?? []).map((row) => [row.name, row]));
    return (completeList ?? []).map((item, index) => {
        const registered = registeredByName.get(item.name) ?? registeredList?.[index];
        const total =
            Number(registered?.cnt) || Number(item.denominator) || 0;
        const done = Number(item.cnt) || 0;
        const percent = calcPercent(done, total);
        return {
            id: item.id ?? index,
            fullName: item.name,
            label: truncateLabel(item.name),
            percent,
            done,
            total,
        };
    });
};

export function HandleChartTooltip({active, payload}) {
    if (!active || !payload?.length) return null;
    const row = payload[0]?.payload;
    if (!row) return null;
    return (
        <div className="rounded-md border border-[#e5e7eb] bg-white px-3 py-2 text-[12px] shadow-sm">
            <p className="mb-1 font-medium text-[#111827]">{row.fullName}</p>
            <p className="text-[#374151]">처리율: {row.percent}%</p>
            <p className="text-[#6b7280]">
                {row.done} / {row.total}건
            </p>
        </div>
    );
}

function TabButton({label, active, onClick}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`rounded-md border px-3 py-1 text-[13px] leading-tight transition-colors ${
                active
                    ? "border-[#3b82f6] text-[#3b82f6]"
                    : "border-[#d1d5db] text-[#9ca3af] hover:border-[#9ca3af]"
            }`}
        >
            {label}
        </button>
    );
}

export function TaskHandleCurrentStatic() {
    const [activeTabId, setActiveTabId] = useState(TABS[0].id);
    const [loading, setLoading] = useState(true);
    const [chartRows, setChartRows] = useState([]);

    const inspectionSearch = useSelector((state) => getDate(state, "점검처리현황"));
    const incidentSearch = useSelector((state) => getDate(state, "장애처리현황"));
    const workSearch = useSelector((state) => getDate(state, "작업처리현황"));
    const serviceSearch = useSelector((state) => getDate(state, "서비스요청 처리현황"));

    const searchByTabId = useMemo(
        () => ({
            inspection: inspectionSearch,
            incident: incidentSearch,
            work: workSearch,
            service: serviceSearch,
        }),
        [inspectionSearch, incidentSearch, workSearch, serviceSearch]
    );

    const loadChart = useCallback(async () => {
        const tab = TABS.find((item) => item.id === activeTabId) ?? TABS[0];
        setLoading(true);
        try {
            const response = await tab.fetch(searchByTabId[tab.id]);
            if (response?.status === 200) {
                const rows = buildHandleChartRows(
                    response.data?.data,
                    response.data?.lineChart
                );
                setChartRows(rows);
            } else {
                setChartRows([]);
            }
        } catch {
            setChartRows([]);
        } finally {
            setLoading(false);
        }
    }, [activeTabId, searchByTabId]);

    useEffect(() => {
        loadChart();
    }, [loadChart]);

    return (
        <div className="relative flex h-full min-h-0 flex-col">
            {loading && <ChartLoadingOverlay />}

            <div className="mb-3 flex shrink-0 flex-wrap items-center justify-between gap-2">
                <h3 className="text-[21px] font-bold text-[#111827]">처리현황</h3>
                <div className="flex flex-wrap gap-1.5">
                    {TABS.map((tab) => (
                        <TabButton
                            key={tab.id}
                            label={tab.label}
                            active={activeTabId === tab.id}
                            onClick={() => setActiveTabId(tab.id)}
                        />
                    ))}
                </div>
            </div>

            <div className="min-h-0 flex-1">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartRows}
                        margin={{top: 8, right: 8, left: 0, bottom: 4}}
                        barCategoryGap="18%"
                    >
                        <CartesianGrid
                            strokeDasharray="4 4"
                            stroke={GRID_COLOR}
                            vertical={false}
                        />
                        <XAxis
                            dataKey="label"
                            angle={-35}
                            textAnchor="end"
                            interval={0}
                            height={30}
                            tickMargin={2}
                            tick={{fill: AXIS_COLOR, fontSize: 11}}
                            axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                            tickLine={false}
                        />
                        <YAxis
                            domain={[0, 100]}
                            ticks={Y_TICKS}
                            width={28}
                            tick={{fill: AXIS_COLOR, fontSize: 11}}
                            axisLine={{stroke: AXIS_COLOR, strokeWidth: 1}}
                            tickLine={false}
                        />
                        <Tooltip content={<HandleChartTooltip />} cursor={{fill: "transparent"}} />
                        {/* 트랙은 Bar.background로 그려 화면 너비에 관계없이 파란 바와 같은 위치/폭으로 정렬되게 함 */}
                        <Bar
                            dataKey="percent"
                            fill={BAR_BLUE}
                            radius={[10, 10, 0, 0]}
                            maxBarSize={36}
                            background={{fill: BAR_TRACK, radius: [10, 10, 0, 0]}}
                            isAnimationActive={!loading}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

export default TaskHandleCurrentStatic;
